import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  EyeIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, Card, Badge, Button, Spinner } from '../ui';
import { Select } from '../forms';
import { StatisticsCard } from '../ui/StatisticsCard';
import { 
  useOverallAssessmentSummary, 
  useAssessments, 
  useChildren, 
  useLearningAreas,
  useClassrooms
} from '../../hooks/api';

interface AnalyticsDashboardProps {
  onDrillDown: (type: string, filters: any) => void;
  onExportData: (type: string, data: any) => void;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  onDrillDown,
  onExportData,
}) => {
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'term' | 'year'>('term');
  const [selectedMetric, setSelectedMetric] = useState<'assessments' | 'observations' | 'progress'>('assessments');
  const [selectedLearningArea, setSelectedLearningArea] = useState<string>('');
  
  const { data: overallSummary, isLoading: summaryLoading } = useOverallAssessmentSummary();
  const { data: assessmentsData, isLoading: assessmentsLoading } = useAssessments({ limit: 100 });
  const { data: childrenData } = useChildren();
  const { data: learningAreas } = useLearningAreas();
  const { data: classroomsData } = useClassrooms();
  
  const children = childrenData?.items || [];
  const assessments = assessmentsData?.items || [];
  const classrooms = classroomsData || [];
  
  // Calculate analytics metrics
  const totalAssessments = assessments.length;
  const publishedAssessments = assessments.filter(a => a.is_published).length;
  const averageScore = assessments.reduce((sum, a) => sum + (a.score || 0), 0) / assessments.length || 0;
  const recentAssessments = assessments.filter(a => {
    const assessmentDate = new Date(a.assessment_date);
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 1);
    return assessmentDate >= cutoff;
  }).length;
  
  // Performance trends (mock data - would come from API)
  const performanceTrends = [
    { period: 'Jan', assessments: 45, average_score: 82 },
    { period: 'Feb', assessments: 52, average_score: 85 },
    { period: 'Mar', assessments: 48, average_score: 83 },
    { period: 'Apr', assessments: 55, average_score: 87 },
    { period: 'May', assessments: 50, average_score: 84 },
    { period: 'Jun', assessments: 58, average_score: 88 },
  ];
  
  // Learning area performance
  const learningAreaPerformance = learningAreas?.map((area, index) => {
    const areaAssessments = assessments.filter(a => a.learning_area_id === area.id);
    const averageScore = areaAssessments.reduce((sum, a) => sum + (a.score || 0), 0) / areaAssessments.length || 0;
    const completionRate = areaAssessments.filter(a => a.score !== undefined).length / areaAssessments.length || 0;

    // Deterministic trend based on index instead of Math.random()
    const trends = ['up', 'down', 'stable'] as const;
    const trend = trends[index % trends.length];

    return {
      area,
      assessments: areaAssessments.length,
      averageScore: Math.round(averageScore),
      completionRate: Math.round(completionRate * 100),
      trend,
    };
  }) || [];
  
  // Classroom performance comparison
  // Note: Since we don't have direct child-classroom relationship in the current data model,
  // we'll show all assessments for now. This should be updated when the API provides proper filtering.
  const classroomPerformance = classrooms.map(classroom => {
    // TODO: Filter assessments by classroom when API supports it
    const classroomAssessments = assessments;
    const averageScore = classroomAssessments.reduce((sum, a) => sum + (a.score || 0), 0) / classroomAssessments.length || 0;
    
    return {
      classroom,
      assessments: classroomAssessments.length,
      averageScore: Math.round(averageScore),
      children: children.length, // TODO: Filter by classroom when available
    };
  });
  
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />;
      case 'down':
        return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
      default:
        return <MinusIcon className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };
  
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <H1 className="mb-2">Analytics Dashboard</H1>
          <Body className="text-gray-600">
            Comprehensive data analysis and performance insights.
          </Body>
        </div>
        <div className="flex items-center space-x-3">
          <Select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as any)}
            options={[
              { value: 'week', label: 'Last Week' },
              { value: 'month', label: 'Last Month' },
              { value: 'term', label: 'This Term' },
              { value: 'year', label: 'This Year' },
            ]}
          />
          <Button variant="secondary" size="sm">
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="primary" size="sm">
            <FunnelIcon className="w-4 h-4 mr-2" />
            Export Data
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatisticsCard
          title="Total Assessments"
          value={totalAssessments}
          change={{
            value: recentAssessments,
            type: 'increase',
            period: 'recent'
          }}
          icon={ClipboardDocumentCheckIcon}
          color="primary"
        />
        <StatisticsCard
          title="Average Score"
          value={`${Math.round(averageScore)}%`}
          change={{
            value: 5,
            type: 'increase',
            period: 'vs last month'
          }}
          icon={ChartBarIcon}
          color="success"
        />
        <StatisticsCard
          title="Published Rate"
          value={`${Math.round((publishedAssessments / totalAssessments) * 100)}%`}
          change={{
            value: 12,
            type: 'increase',
            period: 'vs last month'
          }}
          icon={EyeIcon}
          color="info"
        />
        <StatisticsCard
          title="Active Children"
          value={children.length}
          icon={UserGroupIcon}
          color="secondary"
        />
      </div>

      {/* Performance Trends Chart */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <H2>Performance Trends</H2>
            <Select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value as any)}
              options={[
                { value: 'assessments', label: 'Assessments' },
                { value: 'observations', label: 'Observations' },
                { value: 'progress', label: 'Progress' },
              ]}
            />
          </div>
          
          <div className="space-y-4">
            {performanceTrends.map((trend, index) => (
              <motion.div
                key={trend.period}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <CalendarDaysIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <H3 className="text-sm font-medium">{trend.period}</H3>
                    <Body className="text-xs text-gray-500">
                      {trend.assessments} assessments
                    </Body>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className={`font-semibold ${getScoreColor(trend.average_score)}`}>
                      {trend.average_score}%
                    </div>
                    <div className="text-xs text-gray-500">Average</div>
                  </div>
                  
                  <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        trend.average_score >= 80 ? 'bg-green-500' :
                        trend.average_score >= 60 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${trend.average_score}%` }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Card>

      {/* Learning Area Performance */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <H2>Learning Area Performance</H2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDrillDown('learning-areas', { timeframe })}
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Details
            </Button>
          </div>
          
          <div className="space-y-4">
            {learningAreaPerformance.map((area, index) => (
              <motion.div
                key={area.area.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-gray-50 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <AcademicCapIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <H3 className="font-medium">{area.area.name}</H3>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Badge variant="secondary" size="sm">
                          {area.area.code}
                        </Badge>
                        <span>•</span>
                        <span>{area.assessments} assessments</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className={`font-semibold ${getScoreColor(area.averageScore)}`}>
                        {area.averageScore}%
                      </div>
                      <div className="text-xs text-gray-500">Average Score</div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-semibold text-gray-700">
                        {area.completionRate}%
                      </div>
                      <div className="text-xs text-gray-500">Completion</div>
                    </div>
                    
                    <div className={`flex items-center space-x-1 ${getTrendColor(area.trend)}`}>
                      {getTrendIcon(area.trend)}
                      <span className="text-sm font-medium capitalize">{area.trend}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Card>

      {/* Classroom Comparison */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <H2>Classroom Performance Comparison</H2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDrillDown('classrooms', { timeframe })}
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Details
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classroomPerformance.map((classroom, index) => (
              <motion.div
                key={classroom.classroom.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <UserGroupIcon className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <H3 className="text-sm font-medium">{classroom.classroom.name}</H3>
                      <Body className="text-xs text-gray-500">
                        {classroom.children} children
                      </Body>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Assessments</span>
                      <span className="font-medium">{classroom.assessments}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Average Score</span>
                      <span className={`font-medium ${getScoreColor(classroom.averageScore)}`}>
                        {classroom.averageScore}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          classroom.averageScore >= 80 ? 'bg-green-500' :
                          classroom.averageScore >= 60 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${classroom.averageScore}%` }}
                      />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </Card>

      {/* Insights and Recommendations */}
      <Card>
        <div className="p-6">
          <H2 className="mb-4">Key Insights & Recommendations</H2>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <ArrowTrendingUpIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <H3 className="text-blue-900 font-medium">Strong Performance Trend</H3>
                  <Body className="text-blue-800 text-sm">
                    Overall assessment scores have increased by 8% over the past term. 
                    Mathematics and Language Arts show the strongest improvement.
                  </Body>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <ArrowPathIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <H3 className="text-yellow-900 font-medium">Areas for Improvement</H3>
                  <Body className="text-yellow-800 text-sm">
                    Physical Development assessments have a lower completion rate (65%). 
                    Consider providing additional support and resources for this area.
                  </Body>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <UserGroupIcon className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <H3 className="text-green-900 font-medium">Classroom Excellence</H3>
                  <Body className="text-green-800 text-sm">
                    Room B consistently shows high performance across all learning areas. 
                    Their teaching strategies could be shared with other classrooms.
                  </Body>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
